// ajay:code all 27-08-26
import { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import CharacteristicRow from "./CharacteristicRow";

const emptyCharacteristic = () => ({
  char_no: "",
  product_characteristic: "",
  process_characteristic: "",
  special_char_class: "",
  specification_tolerance: "",
  methods: [],
});

const inputStyle = {
  padding: "6px 8px",
  fontSize: 13,
  borderRadius: 6,
  border: "1px solid var(--border2)",
  background: "var(--surface)",
  color: "var(--text)",
};

export default function StepBlock({
  step,
  index,
  onChange,
  onDelete,
  readOnly,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const set = (field, value) => onChange({ ...step, [field]: value });
  const characteristics = step.characteristics || [];

  const updateCharacteristic = (i, updated) => {
    const next = [...characteristics];
    next[i] = updated;
    set("characteristics", next);
  };
  const addCharacteristic = () =>
    set("characteristics", [...characteristics, emptyCharacteristic()]);
  const removeCharacteristic = (i) =>
    set(
      "characteristics",
      characteristics.filter((_, idx) => idx !== i),
    );

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: "var(--surface2)",
          cursor: "pointer",
          flexWrap: "wrap",
        }}
        onClick={() => setCollapsed((v) => !v)}
      >
        {collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
        <span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 700 }}>
          STEP {index + 1}
        </span>

        {readOnly ? (
          <span style={{ fontSize: 12, color: "var(--text2)", width: 50 }}>
            {step.step_no || "—"}
          </span>
        ) : (
          <input
            type="text"
            value={step.step_no || ""}
            placeholder="No."
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => set("step_no", e.target.value)}
            style={{ ...inputStyle, width: 50 }}
          />
        )}

        {readOnly ? (
          <span
            style={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 160 }}
          >
            {step.step_name || "—"}
          </span>
        ) : (
          <input
            type="text"
            value={step.step_name || ""}
            placeholder="e.g. Injection Moulding"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => set("step_name", e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 160, fontWeight: 700 }}
          />
        )}

        <span style={{ fontSize: 11, color: "var(--text3)" }}>
          {characteristics.length} characteristic
          {characteristics.length !== 1 ? "s" : ""}
        </span>

        {!readOnly && (
          <button
            type="button"
            title="Delete step"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              background: "none",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
            }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>

      {!collapsed && (
        <div style={{ padding: 14 }}>
          <div style={{ marginBottom: 10 }}>
            <div
              style={{
                fontSize: 10,
                color: "var(--text3)",
                fontWeight: 700,
                marginBottom: 3,
              }}
            >
              MACHINE / DEVICE / JIG / TOOLS
            </div>
            {readOnly ? (
              <div style={{ fontSize: 12 }}>{step.machine_tools || "—"}</div>
            ) : (
              <textarea
                rows={2}
                value={step.machine_tools || ""}
                onChange={(e) => set("machine_tools", e.target.value)}
                style={{ ...inputStyle, width: "100%", fontSize: 12 }}
              />
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text3)",
                  fontWeight: 700,
                  marginBottom: 3,
                }}
              >
                CONTROL METHOD
              </div>
              {readOnly ? (
                <div style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {step.control_method || "—"}
                </div>
              ) : (
                <textarea
                  rows={3}
                  value={step.control_method || ""}
                  onChange={(e) => set("control_method", e.target.value)}
                  style={{ ...inputStyle, width: "100%", fontSize: 12 }}
                />
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text3)",
                  fontWeight: 700,
                  marginBottom: 3,
                }}
              >
                REACTION PLAN / CORRECTIVE ACTION
              </div>
              {readOnly ? (
                <div style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {step.reaction_plan || "—"}
                </div>
              ) : (
                <textarea
                  rows={3}
                  value={step.reaction_plan || ""}
                  onChange={(e) => set("reaction_plan", e.target.value)}
                  style={{ ...inputStyle, width: "100%", fontSize: 12 }}
                />
              )}
            </div>
          </div>

          <div
            style={{
              fontSize: 10,
              color: "var(--text3)",
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            CHARACTERISTICS ({characteristics.length})
          </div>
          {characteristics.map((c, i) => (
            <CharacteristicRow
              key={i}
              characteristic={c}
              onChange={(updated) => updateCharacteristic(i, updated)}
              onDelete={() => removeCharacteristic(i)}
              readOnly={readOnly}
            />
          ))}
          {!readOnly && (
            <button
              type="button"
              onClick={addCharacteristic}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "var(--surface2)",
                border: "1px solid var(--border2)",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--accent2)",
                cursor: "pointer",
              }}
            >
              <Plus size={13} /> Add Characteristic
            </button>
          )}
        </div>
      )}
    </div>
  );
}
